import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useReconnectAccount } from '@/api/partner-accounts';
import type { PartnerAccount, AuthFieldDefinition } from '@/types/api';

interface ReconnectAccountDialogProps {
  account: PartnerAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReconnectAccountDialog({
  account,
  open,
  onOpenChange,
}: ReconnectAccountDialogProps): JSX.Element {
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const reconnect = useReconnectAccount();

  const authFields: AuthFieldDefinition[] = account.partnerType?.authFields ?? [];
  const partnerTypeName = account.partnerType?.name ?? 'partner';

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setCredentials({});
      setError(null);
    }
  }, [open]);

  const handleCredentialChange = (key: string, value: string) => {
    setCredentials((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    for (const field of authFields) {
      if (field.required && !credentials[field.key]) {
        setError(`${field.label} is required`);
        return;
      }
    }

    try {
      await reconnect.mutateAsync({
        id: account.id,
        data: { credentials },
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reconnect account');
    }
  };

  const handleClose = () => {
    if (!reconnect.isPending) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reconnect {account.name}</DialogTitle>
          <DialogDescription>
            Enter new credentials to restore the connection to {partnerTypeName}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {authFields.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No credential fields configured for this partner type.
              </p>
            ) : (
              authFields.map((field) => (
                <div key={field.key} className="space-y-2">
                  <Label htmlFor={field.key}>
                    {field.label}
                    {field.required && <span className="text-destructive"> *</span>}
                  </Label>
                  <Input
                    id={field.key}
                    type={field.type}
                    placeholder={field.placeholder}
                    value={credentials[field.key] ?? ''}
                    onChange={(e) => { handleCredentialChange(field.key, e.target.value); }}
                    disabled={reconnect.isPending}
                    data-testid={`reconnect-${field.key}-input`}
                  />
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={reconnect.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={reconnect.isPending || authFields.length === 0} data-testid="reconnect-submit-btn">
              {reconnect.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reconnect
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
